import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
    applySelectedProjectDetail,
    GetCurrentProjectData,
    GetMergedCompanyProfile,
    PatchProjectData,
    PostProjectData,
} from '../../redux/projectDataSlice';
import { GetDataroomItemInfo } from '../../redux/dataroomSlice';
import { applyCompanyEnrichment } from '../../utils/companyEnrichment';
import { getCreateStep1FieldPolicy } from '../../utils/projectTypeFieldPolicy';
import { ensureHttpsWebsite, isWebsiteUrl } from '../../utils/websiteUrl';
import { aiProcessingZoneFromForm } from '../../utils/projectAiProcessing';
import {
    fieldMetaFromSources,
    getProjectFilesStatus,
    retryProjectFiles,
    suggestEngagement,
    suggestIrl,
    suggestOrganisation,
    uploadProjectFile,
} from './projectSetupApi';
import { applyOrganisationSuggestion } from './setupSuggestionMapping';
import CreateProjectStep1, { createInitialStep1FormValues } from './CreateProjectStep1';
import CreateProjectStep2 from './CreateProjectStep2';
import CreateProjectPreparing from './CreateProjectPreparing';
import CreateProjectStep3 from './CreateProjectStep3';
import CreateProjectStep5 from './CreateProjectStep5';
import CreateProjectStep6 from './CreateProjectStep6';
import CreateProjectCreated from './CreateProjectCreated';
import SetupWorkstreams from './SetupWorkstreams';
import SetupVdrStructure from './SetupVdrStructure';
import SetupIrlPreparing from './SetupIrlPreparing';
import SetupIrl from './SetupIrl';
import SetupProjectReady from './SetupProjectReady';
import SetupTeamFlow from './SetupTeamFlow';
import styles from './css/index.module.css';

const CURRENCY_SYMBOL = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    '£': '£',
    $: '$',
    '€': '€',
};

function parseOptionalInt(value) {
    const parsed = parseInt(String(value || '').replace(/[^0-9]/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : null;
}

function buildStep1Body(formData) {
    const policy = getCreateStep1FieldPolicy(formData?.project_type);
    let projectDeadline;
    try {
        projectDeadline = formData?.project_deadline ? format(new Date(formData.project_deadline), 'dd-MM-yyyy') : undefined;
    } catch {
        projectDeadline = undefined;
    }
    return {
        project_name: formData.project_name,
        project_type: formData.project_type,
        company_name: formData.company_name,
        website: formData.website?.trim() ? ensureHttpsWebsite(formData.website) : null,
        investment_type: policy.investmentType === 'hidden' ? '' : formData.investment_type || '',
        investor_name: policy.showInvestor ? formData.investor_name || '' : '',
        project_deadline: projectDeadline,
        ai_processing_zone: aiProcessingZoneFromForm(formData.process_ai_in_eu),
        is_draft: true,
    };
}

function buildOrgPatch(formData) {
    const revenue = parseOptionalInt(formData?.revenue);
    const establishedYear = parseOptionalInt(formData?.established_year);
    const patch = {
        company_name: formData.company_name || '',
        website: formData.website?.trim() ? ensureHttpsWebsite(formData.website) : null,
        sector: formData.sector || '',
        description: formData.description || '',
        company_size: formData.company_size || '',
        established_year: establishedYear,
        customer_stage: formData.customer_stage || '',
        customer_type: formData.customer_type || '',
        customer_segment: formData.customer_segment || '',
        currency: CURRENCY_SYMBOL[formData.currency] || '£',
        revenue: revenue ?? 0,
        linkedin: formData.linkedin || '',
        product_website: formData.product_website || '',
        companieshouse: formData.companieshouse || '',
    };
    if (formData.logo) {
        patch.logo_url = formData.logo;
    }
    return patch;
}

function buildEngagementPatch(formData) {
    return {
        engagement_objective: formData.engagement_objective?.trim() || '',
        scope_areas: Array.isArray(formData.scope_areas) ? formData.scope_areas : [],
    };
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

const FILES_STATUS_POLL_MS = 5000;

function websiteForMergedProfile(form) {
    const website = ensureHttpsWebsite(form?.website);
    if (!website || website === '-') return '';
    return website;
}

function withNormalizedWebsite(form) {
    const website = ensureHttpsWebsite(form?.website);
    if (!website || website === form?.website || !isWebsiteUrl(website)) return form;
    return { ...form, website };
}

function companyNameForMergedProfile(form) {
    return String(form?.company_name || '').trim();
}

function engagementFromSuggestion(engagement) {
    const recommended = (engagement?.recommended_scope_areas || []).map(item => item?.name || item).filter(Boolean);
    const objective =
        engagement?.engagement_objective_text ||
        (typeof engagement?.engagement_objective === 'string'
            ? engagement.engagement_objective
            : engagement?.engagement_objective?.text) ||
        '';
    return { recommended, objective, suggestion: engagement };
}

function applyPatchWithoutOverwrite(current, patch, previous = {}) {
    const next = { ...current };
    Object.entries(patch || {}).forEach(([key, value]) => {
        if (value == null || value === '') return;
        const currentValue = current[key];
        const previousValue = previous[key];
        const untouched = currentValue == null || currentValue === '' || currentValue === previousValue;
        if (untouched) next[key] = value;
    });
    return next;
}

const FAILED_FILE_AGGREGATES = new Set(['FAILED', 'PARTIAL_FAILED']);
const READY_FILE_AGGREGATES = new Set(['READY', 'NO_DOCUMENTS']);

const SHOW_SETUP_WORKSTREAMS_FOR_TESTING = false;

const NewProjectFlow = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { projectDetailButtonLoading } = useSelector(state => state.projectData);
    const [step, setStep] = React.useState(1);
    const [preparing, setPreparing] = React.useState(false);
    const [created, setCreated] = React.useState(false);
    const [setupStep, setSetupStep] = React.useState(SHOW_SETUP_WORKSTREAMS_FOR_TESTING ? 1 : 0);
    const [confirmedWorkstreams, setConfirmedWorkstreams] = React.useState([]);
    const [preparingIrl, setPreparingIrl] = React.useState(false);
    const [irlSuggestion, setIrlSuggestion] = React.useState(null);
    const [setupSummary, setSetupSummary] = React.useState({ workstreams: 0, folders: 0, requests: 0 });
    const [teamSetup, setTeamSetup] = React.useState(false);
    const [createdProject, setCreatedProject] = React.useState(null);
    const [returnToReview, setReturnToReview] = React.useState(false);
    const [formData, setFormData] = React.useState(createInitialStep1FormValues);
    const [stepBusy, setStepBusy] = React.useState(false);
    const [preparingItems, setPreparingItems] = React.useState([]);
    const [documentBanner, setDocumentBanner] = React.useState(null);
    const [bannerDismissed, setBannerDismissed] = React.useState(false);
    const [documentsOmitted, setDocumentsOmitted] = React.useState(false);
    const [suggestionRefreshKey, setSuggestionRefreshKey] = React.useState(0);
    const [refreshingRecommendations, setRefreshingRecommendations] = React.useState(false);
    const formDataRef = React.useRef(formData);
    const createdProjectRef = React.useRef(createdProject);
    const skipDocumentsRef = React.useRef(false);
    const retryDocumentsRef = React.useRef(0);
    const orgSuggestionRef = React.useRef({});
    const engagementSuggestionRef = React.useRef({});
    const mergedProfilePromiseRef = React.useRef(null);
    const mergedProfileKeyRef = React.useRef('');
    const mergedProfileSettledRef = React.useRef(false);

    React.useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    React.useEffect(() => {
        createdProjectRef.current = createdProject;
    }, [createdProject]);

    const patchFormData = React.useCallback(patch => {
        setFormData(prev => ({ ...prev, ...patch }));
    }, []);

    const rememberProject = React.useCallback(
        project => {
            if (!project?.id) return;
            setCreatedProject(project);
            createdProjectRef.current = project;
            dispatch(applySelectedProjectDetail(project));
        },
        [dispatch],
    );

    const startMergedProfilePrefetch = React.useCallback(
        form => {
            const website = websiteForMergedProfile(form);
            const companyName = companyNameForMergedProfile(form);
            const key = `${companyName}|${website}`;
            if (mergedProfilePromiseRef.current && mergedProfileKeyRef.current === key) {
                return mergedProfilePromiseRef.current;
            }
            mergedProfileKeyRef.current = key;
            mergedProfileSettledRef.current = false;
            const promise = dispatch(
                GetMergedCompanyProfile({
                    website,
                    company_name: companyName,
                    ai_processing_zone: aiProcessingZoneFromForm(form?.process_ai_in_eu),
                    background: true,
                }),
            ).finally(() => {
                mergedProfileSettledRef.current = true;
            });
            mergedProfilePromiseRef.current = promise;
            return promise;
        },
        [dispatch],
    );

    const patchDraft = React.useCallback(
        async data => {
            const project = createdProjectRef.current;
            if (!project?.id) return null;
            const response = await dispatch(
                PatchProjectData({
                    id: project.id,
                    data,
                    suppressSuccessToast: true,
                }),
            );
            if (response?.type !== 'projectData/patchProjectData/fulfilled') {
                return null;
            }
            const next = response.payload?.data || project;
            rememberProject(next);
            return next;
        },
        [dispatch, rememberProject],
    );

    const handleCancel = async () => {
        const project = createdProjectRef.current;
        if (project?.id && project?.is_draft) {
            await dispatch(
                PatchProjectData({
                    id: project.id,
                    data: { soft_delete: true },
                    suppressSuccessToast: true,
                }),
            );
        }
        navigate('/project');
    };

    const clearReadyBannerOnAdvance = () => {
        setDocumentBanner(current => (current === 'ready' ? null : current));
    };

    const goForwardFrom = fromStep => {
        clearReadyBannerOnAdvance();
        if (returnToReview) {
            setReturnToReview(false);
            setStep(6);
            return;
        }
        if (fromStep === 1) setStep(2);
        else if (fromStep === 3) setStep(5);
        else if (fromStep === 5) setStep(6);
    };

    const handleEdit = targetStep => {
        setReturnToReview(true);
        setStep(targetStep);
    };

    const handleBack = fromStep => {
        if (returnToReview) {
            setReturnToReview(false);
            setStep(6);
            return;
        }
        if (fromStep === 2) setStep(1);
        else if (fromStep === 3) setStep(2);
        else if (fromStep === 5) setStep(3);
        else if (fromStep === 6) setStep(5);
    };

    const handleStep1Continue = async () => {
        if (stepBusy) return;
        setStepBusy(true);
        const current = withNormalizedWebsite(formDataRef.current);
        if (current !== formDataRef.current) {
            formDataRef.current = current;
            setFormData(current);
        }
        const body = buildStep1Body(current);
        let ok = false;
        try {
            if (createdProjectRef.current?.id) {
                ok = Boolean(await patchDraft(body));
            } else {
                const createResponse = await dispatch(
                    PostProjectData({
                        body,
                        suppressSuccessToast: true,
                    }),
                );
                if (createResponse?.type === 'projectData/postProjectData/fulfilled') {
                    rememberProject(createResponse.payload?.data);
                    ok = true;
                }
            }
            if (ok) {
                startMergedProfilePrefetch(current);
                goForwardFrom(1);
            }
        } finally {
            setStepBusy(false);
        }
    };

    const uploadPendingContextFiles = React.useCallback(async () => {
        const project = createdProjectRef.current;
        const files = Array.isArray(formDataRef.current.context_files) ? formDataRef.current.context_files : [];
        if (!project?.id) return files;
        for (const file of files) {
            if (!file || file.uploadedId) continue;
            try {
                const uploaded = await uploadProjectFile(project.id, file);
                file.uploadedId = uploaded?.id;
            } catch {
                // Preparing still continues if a context file fails to upload.
            }
        }
        setFormData(prev => ({ ...prev, context_files: files }));
        formDataRef.current = { ...formDataRef.current, context_files: files };
        return files;
    }, []);

    const handleStep2Next = async () => {
        if (stepBusy) return;
        setStepBusy(true);
        try {
            await uploadPendingContextFiles();
        } finally {
            setStepBusy(false);
        }
        setPreparing(true);
    };

    const updatePreparingItems = React.useCallback(updater => {
        setPreparingItems(prev => (typeof updater === 'function' ? updater(prev) : updater));
    }, []);

    const markPreparing = React.useCallback((key, status, extra = {}) => {
        updatePreparingItems(prev => prev.map(item => (item.key === key ? { ...item, status, ...extra } : item)));
    }, [updatePreparingItems]);

    const waitForDocuments = React.useCallback(
        async (projectId, { expectFiles = false } = {}) => {
            skipDocumentsRef.current = false;
            while (!skipDocumentsRef.current) {
                let status;
                try {
                    status = await getProjectFilesStatus(projectId);
                } catch {
                    status = { aggregate: 'PROCESSING', ready_count: 0, total: 0, files: [] };
                }
                const readyCount = status.ready_count || 0;
                const total = status.total || 0;
                if (status.aggregate === 'NO_DOCUMENTS' && expectFiles) {
                    status = { ...status, aggregate: 'FAILED', files: status.files || [] };
                }
                if (READY_FILE_AGGREGATES.has(status.aggregate)) {
                    markPreparing('docs', 'done', {
                        detail: total ? `${readyCount} of ${total} documents ready` : '',
                        longRunning: false,
                    });
                    return { skipped: false, ready: true, status };
                }
                if (FAILED_FILE_AGGREGATES.has(status.aggregate)) {
                    markPreparing('docs', 'error', {
                        label: "Some project documents couldn't be processed.",
                        detail: '',
                        longRunning: false,
                    });
                    const retryAt = retryDocumentsRef.current;
                    while (!skipDocumentsRef.current && retryDocumentsRef.current === retryAt) {
                        await sleep(250);
                    }
                    if (skipDocumentsRef.current) {
                        return { skipped: true, ready: false, status };
                    }
                    const failedIds = (status.files || [])
                        .filter(file => file.status === 'failed')
                        .map(file => file.file_id);
                    markPreparing('docs', 'active', {
                        label: 'Processing uploaded project documents',
                        detail: `${readyCount} of ${total} documents ready`,
                        longRunning: true,
                    });
                    try {
                        await uploadPendingContextFiles();
                        await retryProjectFiles(projectId, failedIds);
                    } catch {
                        // Poll loop will surface another error if retry did not recover.
                    }
                    continue;
                }
                markPreparing('docs', 'active', {
                    detail: total ? `${readyCount} of ${total} documents ready` : '',
                    longRunning: true,
                });
                await sleep(FILES_STATUS_POLL_MS);
            }
            return { skipped: true, ready: false, status: null };
        },
        [markPreparing, uploadPendingContextFiles],
    );

    const runPreparing = React.useCallback(async () => {
        const project = createdProjectRef.current;
        const current = formDataRef.current;
        if (!project?.id) return;

        skipDocumentsRef.current = false;
        const pendingFiles = Array.isArray(current.context_files) ? current.context_files : [];
        const hasDocuments = pendingFiles.length > 0;
        const gatherAlreadyDone = mergedProfileSettledRef.current;
        setDocumentsOmitted(false);
        setDocumentBanner(null);
        setBannerDismissed(false);
        setPreparingItems([
            { key: 'save', label: 'Saving project details', status: 'active' },
            { key: 'gather', label: 'Gathering organisation information', status: gatherAlreadyDone ? 'done' : 'pending' },
            ...(hasDocuments
                ? [{ key: 'docs', label: 'Processing uploaded project documents', status: 'pending', longRunning: true }]
                : []),
            { key: 'org', label: 'Preparing organisation context', status: 'pending' },
        ]);

        const files = await uploadPendingContextFiles();
        markPreparing('save', 'done');
        if (!mergedProfileSettledRef.current) {
            markPreparing('gather', 'active');
        }

        let companyDetails = {};
        let fieldMeta = {};
        let coresignalEnrichment = {};
        let publicEnrichment = {};
        try {
            const merged = await startMergedProfilePrefetch(current);
            if (merged?.type === 'GET_MERGED_COMPANY_PROFILE/fulfilled') {
                companyDetails = merged?.payload?.data?.company_info || {};
                fieldMeta = fieldMetaFromSources(merged?.payload?.data?.field_meta || {});
                coresignalEnrichment = merged?.payload?.data?.coresignal_enrichment || {};
                publicEnrichment = merged?.payload?.data?.public_ai_enrichment || {};
            }
        } catch {
            companyDetails = {};
        }
        markPreparing('gather', 'done');

        let documentsUsed = !hasDocuments;
        if (hasDocuments) {
            markPreparing('docs', 'active', { longRunning: true });
            const waitResult = await waitForDocuments(project.id, { expectFiles: true });
            documentsUsed = Boolean(waitResult.ready) && !waitResult.skipped;
            setDocumentsOmitted(!documentsUsed);
        }

        markPreparing('org', 'active');
        const enriched = applyCompanyEnrichment({ ...current, fieldMeta }, companyDetails, fieldMeta);
        let nextForm = {
            ...enriched,
            fieldMeta,
            customer_segment: current.customer_segment || enriched.customer_segment || '',
            documentsUsedInGeneration: documentsUsed,
        };

        try {
            const organisation = await suggestOrganisation(project.id, {
                coresignal_enrichment: coresignalEnrichment,
                public_ai_enrichment: publicEnrichment,
            });
            const orgPatch = organisation?.project_patch || {};
            const orgMeta = fieldMetaFromSources(organisation?.field_meta || {});
            orgSuggestionRef.current = orgPatch;
            nextForm = applyOrganisationSuggestion(nextForm, orgPatch, orgMeta);
            nextForm = {
                ...nextForm,
                fieldMeta: {
                    ...fieldMeta,
                    ...orgMeta,
                    ...nextForm.fieldMeta,
                    ...(orgPatch.customer_stage && !orgMeta.customer_stage ? { customer_stage: { source: 'Lens' } } : {}),
                    ...(orgPatch.customer_type && !orgMeta.customer_type ? { customer_type: { source: 'Lens' } } : {}),
                    ...(orgPatch.customer_segment && !orgMeta.customer_segment ? { customer_segment: { source: 'Lens' } } : {}),
                },
            };
        } catch {
            // Keep the form values already gathered. Nothing is written to the project until review.
        }

        markPreparing('org', 'done');
        if (documentsUsed && hasDocuments) {
            setDocumentBanner('ready');
        }
        setFormData(prev => ({ ...prev, ...nextForm, context_files: files }));
        formDataRef.current = { ...formDataRef.current, ...nextForm, context_files: files };
    }, [markPreparing, startMergedProfilePrefetch, uploadPendingContextFiles, waitForDocuments]);

    const handlePreparingComplete = React.useCallback(() => {
        setPreparing(false);
        setStep(3);
    }, []);

    const runPreparingRef = React.useRef(runPreparing);
    React.useEffect(() => {
        runPreparingRef.current = runPreparing;
    }, [runPreparing]);

    React.useEffect(() => {
        if (!preparing) return undefined;
        let cancelled = false;
        const run = async () => {
            try {
                await runPreparingRef.current();
            } finally {
                if (!cancelled) handlePreparingComplete();
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [handlePreparingComplete, preparing]);

    const handleSkipDocuments = React.useCallback(() => {
        skipDocumentsRef.current = true;
        setDocumentsOmitted(true);
    }, []);

    const handleRetryDocuments = React.useCallback(() => {
        retryDocumentsRef.current += 1;
    }, []);

    const dismissDocumentBanner = React.useCallback(() => {
        setDocumentBanner(null);
        setBannerDismissed(true);
    }, []);

    const refreshOrganisationSuggestions = React.useCallback(async () => {
        const project = createdProjectRef.current;
        if (!project?.id) return;
        try {
            const organisation = await suggestOrganisation(project.id);
            const orgPatch = organisation?.project_patch || {};
            const orgMeta = fieldMetaFromSources(organisation?.field_meta || {});
            const current = formDataRef.current;
            const nextForm = applyOrganisationSuggestion(current, orgPatch, orgMeta, orgSuggestionRef.current);
            orgSuggestionRef.current = { ...orgSuggestionRef.current, ...orgPatch };
            setFormData(nextForm);
            formDataRef.current = nextForm;
        } catch {
            // Keep current organisation values if refresh fails.
        }
        setSuggestionRefreshKey(key => key + 1);
    }, []);

    const refreshEngagementSuggestions = React.useCallback(async () => {
        const project = createdProjectRef.current;
        if (!project?.id) return;
        try {
            const engagement = await suggestEngagement(project.id);
            const mapped = engagementFromSuggestion(engagement);
            const current = formDataRef.current;
            const nextForm = applyPatchWithoutOverwrite(
                {
                    ...current,
                    engagementSuggestion: mapped.suggestion,
                    engagementSeeded: true,
                },
                {
                    engagement_objective: mapped.objective,
                    scope_areas: mapped.recommended,
                },
                engagementSuggestionRef.current,
            );
            engagementSuggestionRef.current = {
                engagement_objective: mapped.objective,
                scope_areas: mapped.recommended,
            };
            setFormData(nextForm);
            formDataRef.current = nextForm;
        } catch {
            // Keep current engagement values if refresh fails.
        }
        setSuggestionRefreshKey(key => key + 1);
    }, []);

    const handleRecommendationsRefreshed = React.useCallback(() => {
        setRefreshingRecommendations(false);
        setDocumentBanner(null);
        setBannerDismissed(true);
        setDocumentsOmitted(false);
    }, []);

    const handleRefreshRecommendations = React.useCallback(async () => {
        if (refreshingRecommendations) return;
        setRefreshingRecommendations(true);
        if (setupStep) {
            setSuggestionRefreshKey(key => key + 1);
            return;
        }
        try {
            if (step === 5) {
                await refreshEngagementSuggestions();
            } else if (step === 6) {
                await refreshOrganisationSuggestions();
                await refreshEngagementSuggestions();
            } else {
                await refreshOrganisationSuggestions();
            }
        } finally {
            handleRecommendationsRefreshed();
        }
    }, [
        handleRecommendationsRefreshed,
        refreshEngagementSuggestions,
        refreshOrganisationSuggestions,
        refreshingRecommendations,
        setupStep,
        step,
    ]);

    React.useEffect(() => {
        if (!documentsOmitted || !createdProject?.id || bannerDismissed || documentBanner === 'stale') {
            return undefined;
        }
        let cancelled = false;
        const poll = async () => {
            while (!cancelled) {
                try {
                    const status = await getProjectFilesStatus(createdProject.id);
                    if (!cancelled && READY_FILE_AGGREGATES.has(status.aggregate) && status.total > 0) {
                        setDocumentBanner('stale');
                        setDocumentsOmitted(false);
                        return;
                    }
                } catch {
                    // Keep polling; Azure presence can lag.
                }
                if (cancelled) return;
                await sleep(FILES_STATUS_POLL_MS);
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [bannerDismissed, createdProject?.id, documentBanner, documentsOmitted]);

    const runIrlPreparing = React.useCallback(async () => {
        const project = createdProjectRef.current;
        const workstreams = confirmedWorkstreams || [];
        if (!project?.id || !workstreams.length) {
            setIrlSuggestion(null);
            return;
        }
        const folderResults = await Promise.all(
            workstreams.map(async ws => {
                const foldersRes = await dispatch(GetDataroomItemInfo(ws.id));
                const folders = (foldersRes?.payload?.data || []).map(folder => ({
                    id: folder.id,
                    name: folder.name,
                }));
                return { id: ws.id, name: ws.name, folders };
            }),
        );
        try {
            const suggestion = await suggestIrl(project.id, folderResults);
            setIrlSuggestion(suggestion || null);
        } catch {
            setIrlSuggestion(null);
        }
    }, [confirmedWorkstreams, dispatch]);

    const handleIrlPreparingComplete = React.useCallback(() => {
        setPreparingIrl(false);
        setSetupStep(3);
    }, []);

    const handleStep3Continue = async () => {
        if (stepBusy) return;
        setStepBusy(true);
        const current = withNormalizedWebsite(formDataRef.current);
        if (current !== formDataRef.current) {
            formDataRef.current = current;
            setFormData(current);
        }
        try {
            const ok = await patchDraft(buildOrgPatch(current));
            if (!ok) return;
            try {
                const engagement = await suggestEngagement(createdProjectRef.current.id);
                const mapped = engagementFromSuggestion(engagement);
                engagementSuggestionRef.current = {
                    engagement_objective: mapped.objective,
                    scope_areas: mapped.recommended,
                };
                const nextForm = {
                    engagementSuggestion: mapped.suggestion,
                    engagement_objective: mapped.objective,
                    scope_areas: mapped.recommended,
                    engagementSeeded: true,
                };
                setFormData(prev => ({ ...prev, ...nextForm }));
                formDataRef.current = { ...formDataRef.current, ...nextForm };
            } catch {
                // Step 5 still has a local fallback seed.
            }
            goForwardFrom(3);
        } finally {
            setStepBusy(false);
        }
    };

    const handleStep5Continue = async () => {
        if (stepBusy) return;
        setStepBusy(true);
        try {
            const ok = await patchDraft(buildEngagementPatch(formDataRef.current));
            if (ok) goForwardFrom(5);
        } finally {
            setStepBusy(false);
        }
    };

    const handleConfirmProject = async () => {
        const confirmed = await patchDraft({
            ...buildOrgPatch(formDataRef.current),
            ...buildEngagementPatch(formDataRef.current),
            is_draft: false,
        });
        if (!confirmed) return;
        clearReadyBannerOnAdvance();
        dispatch(GetCurrentProjectData());
        setCreatedProject({ ...confirmed, is_draft: false });
        setCreated(true);
    };

    const goToProjectHome = () => navigate('/overview');

    const startWorkspaceSetup = () => {
        setCreated(false);
        setSetupStep(1);
    };

    const wizardBusy = stepBusy || projectDetailButtonLoading;

    return (
        <div className={styles.page}>
            {preparing && (
                <CreateProjectPreparing
                    items={preparingItems}
                    subtitle={
                        (formData.context_files || []).length
                            ? 'Lens is gathering organisation information and processing the project documents you provided.'
                            : 'Lens is finding company information and preparing a draft project context.'
                    }
                    onSkipDocuments={handleSkipDocuments}
                    onRetryDocuments={handleRetryDocuments}
                />
            )}
            {created && <CreateProjectCreated onSetup={startWorkspaceSetup} onSkip={goToProjectHome} />}
            {preparingIrl && <SetupIrlPreparing prepare={runIrlPreparing} onComplete={handleIrlPreparingComplete} />}
            {!created && setupStep === 1 && !preparingIrl && (
                <SetupWorkstreams
                    project={createdProject}
                    scopeAreas={formData.scope_areas}
                    documentBanner={documentBanner}
                    onDismissDocumentBanner={dismissDocumentBanner}
                    onRefreshRecommendations={handleRefreshRecommendations}
                    refreshingRecommendations={refreshingRecommendations}
                    onRefreshSettled={refreshingRecommendations ? handleRecommendationsRefreshed : undefined}
                    refreshToken={suggestionRefreshKey}
                    onContinue={selected => {
                        clearReadyBannerOnAdvance();
                        setConfirmedWorkstreams(selected || []);
                        setSetupSummary(prev => ({ ...prev, workstreams: (selected || []).length }));
                        setSetupStep(2);
                    }}
                    onExit={goToProjectHome}
                />
            )}
            {!created && setupStep === 2 && !preparingIrl && (
                <SetupVdrStructure
                    project={createdProject}
                    workstreams={confirmedWorkstreams}
                    documentBanner={documentBanner}
                    onDismissDocumentBanner={dismissDocumentBanner}
                    onRefreshRecommendations={handleRefreshRecommendations}
                    refreshingRecommendations={refreshingRecommendations}
                    onRefreshSettled={refreshingRecommendations ? handleRecommendationsRefreshed : undefined}
                    refreshToken={suggestionRefreshKey}
                    onContinue={folderCount => {
                        clearReadyBannerOnAdvance();
                        setSetupSummary(prev => ({ ...prev, folders: folderCount || 0 }));
                        setPreparingIrl(true);
                    }}
                    onExit={goToProjectHome}
                />
            )}
            {!created && setupStep === 3 && !preparingIrl && (
                <SetupIrl
                    project={createdProject}
                    workstreams={confirmedWorkstreams}
                    irlSuggestion={irlSuggestion}
                    documentBanner={documentBanner}
                    onDismissDocumentBanner={dismissDocumentBanner}
                    onRefreshRecommendations={handleRefreshRecommendations}
                    refreshingRecommendations={refreshingRecommendations}
                    onRefreshSettled={refreshingRecommendations ? handleRecommendationsRefreshed : undefined}
                    refreshToken={suggestionRefreshKey}
                    onContinue={requestCount => {
                        clearReadyBannerOnAdvance();
                        setSetupSummary(prev => ({ ...prev, requests: requestCount || 0 }));
                        setSetupStep(4);
                    }}
                    onExit={goToProjectHome}
                />
            )}
            {!created && setupStep === 4 && !preparingIrl && !teamSetup && (
                <SetupProjectReady
                    workstreamCount={setupSummary.workstreams}
                    folderCount={setupSummary.folders}
                    requestCount={setupSummary.requests}
                    onEnter={goToProjectHome}
                    onSetupTeam={() => setTeamSetup(true)}
                />
            )}
            {!created && teamSetup && (
                <SetupTeamFlow
                    project={createdProject}
                    workstreams={confirmedWorkstreams}
                    onBackToReady={() => setTeamSetup(false)}
                    onEnterProject={goToProjectHome}
                />
            )}
            {!preparing && !created && !setupStep && !preparingIrl && step === 1 && (
                <CreateProjectStep1
                    formData={formData}
                    onChange={patchFormData}
                    onContinue={handleStep1Continue}
                    onCancel={handleCancel}
                    continueLoading={wizardBusy}
                />
            )}
            {!preparing && !created && !setupStep && !preparingIrl && step === 2 && (
                <CreateProjectStep2
                    files={formData.context_files}
                    onChange={patchFormData}
                    onContinue={handleStep2Next}
                    onSkip={handleStep2Next}
                    onBack={() => handleBack(2)}
                    continueLoading={wizardBusy}
                />
            )}
            {!preparing && !created && !setupStep && !preparingIrl && step === 3 && (
                <CreateProjectStep3
                    formData={formData}
                    onChange={patchFormData}
                    onContinue={handleStep3Continue}
                    onBack={() => handleBack(3)}
                    continueLoading={wizardBusy}
                    documentBanner={documentBanner}
                    onDismissDocumentBanner={dismissDocumentBanner}
                    onRefreshRecommendations={handleRefreshRecommendations}
                    refreshingRecommendations={refreshingRecommendations}
                />
            )}
            {!preparing && !created && !setupStep && !preparingIrl && step === 5 && (
                <CreateProjectStep5
                    formData={formData}
                    onChange={patchFormData}
                    onContinue={handleStep5Continue}
                    onBack={() => handleBack(5)}
                    continueLoading={wizardBusy}
                    documentBanner={documentBanner}
                    onDismissDocumentBanner={dismissDocumentBanner}
                    onRefreshRecommendations={handleRefreshRecommendations}
                    refreshingRecommendations={refreshingRecommendations}
                />
            )}
            {!preparing && !created && !setupStep && !preparingIrl && step === 6 && (
                <CreateProjectStep6
                    formData={formData}
                    creating={wizardBusy}
                    onCreate={handleConfirmProject}
                    onBack={() => handleBack(6)}
                    onEdit={handleEdit}
                    documentBanner={documentBanner}
                    onDismissDocumentBanner={dismissDocumentBanner}
                    onRefreshRecommendations={handleRefreshRecommendations}
                    refreshingRecommendations={refreshingRecommendations}
                />
            )}
        </div>
    );
};

export default NewProjectFlow;
